import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link, useNavigate, useSearch, useParams } from '@tanstack/react-router'
import type { LinkProps } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from './ui/icon'
import { mdiAccountGroup, mdiAlertCircleOutline, mdiClose, mdiLoading, mdiMagnify, mdiTag, mdiTicketOutline } from '@mdi/js'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Badge } from './ui/badge'
import { useApi } from '../api/hooks'
import { useTags } from '../api/tags'
import type { Tag as TagModel } from '../api/tags'
import { TagPicker } from './tags/TagPicker'
import { TagBadgeLink } from './tags/TagBadgeLink'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useAuth } from '../hooks/useAuth'
import { useLocale } from '../contexts/LocaleContext'
import { getLocalizedName } from '../lib/localization'
import { AssetTypeTile } from './assets/AssetTypeTile'
import { SECTION_ICONS } from '../lib/section-icons'

// The single source of truth for which entity types search can return —
// widen this FIRST when the backend indexes a new one, so the compiler
// points at every place that needs to learn about it (spec 2026-09-18-07).
const ENTITY_TYPES = ['asset', 'document', 'user', 'issue', 'capex_plan', 'contract', 'tag', 'conversation'] as const
type EntityType = typeof ENTITY_TYPES[number]

interface SearchResult {
  entity_type: EntityType
  entity_uid: string
  title: string
  description?: string
  score: number
  highlighted_title?: string
  highlighted_description?: string
  // Present only for entity_type=asset — the asset's own type, since "asset"
  // alone does not say whether a result is a building, a lot, or a boiler
  // (spec 2026-09-16-07). Show only, never a filter.
  entity_subtype?: {
    code: string
    name: Record<string, string>
  }
}

interface SearchResponse {
  results: SearchResult[]
  total: number
  limit: number
  offset: number
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

const ENTITY_TYPE_KEYS = ['(all)', ...ENTITY_TYPES] as const
const SCOPE_KEYS = ['title', 'properties', 'content'] as const

// Record over EntityType (not `as const`): a type added to ENTITY_TYPES above
// without an entry here fails to compile instead of rendering with no icon.
const ENTITY_TYPE_ICONS: Record<EntityType, string> = {
  asset: SECTION_ICONS.assets,
  document: SECTION_ICONS.documents,
  user: mdiAccountGroup,
  issue: mdiTicketOutline,
  capex_plan: SECTION_ICONS.capex,
  contract: SECTION_ICONS.contracts,
  tag: mdiTag,
  conversation: SECTION_ICONS.conversations,
}

// A tag is addressed in the URL by its code, falling back to its uid for a
// code-less tag — the same "uid or code" pair the API accepts.
const tagKeyOf = (tag: TagModel): string => tag.code || tag.uid || ''

interface ResultTarget {
  to: string
  params: Record<string, string>
  search?: Record<string, string | undefined>
}

// Where a result LIVES, as data — so the row can be rendered as a real
// `<a href>` and the browser gets middle-click, cmd+click, "copy link
// address" and Tab+Enter for free (spec 2026-09-22-01). Returns null for an
// entity type the frontend has no route for; such a row stays non-clickable
// rather than linking nowhere.
function resultTarget(result: SearchResult, org: string, currentUserUid?: string): ResultTarget | null {
  switch (result.entity_type) {
    case 'asset':
      return { to: '/$org/assets/$assetUid', params: { org, assetUid: result.entity_uid } }
    case 'document':
      return { to: '/$org/documents/$docUid', params: { org, docUid: result.entity_uid } }
    case 'user':
      // Own profile goes to /me/settings, everyone else to their uid page.
      return currentUserUid && result.entity_uid === currentUserUid
        ? { to: '/$org/users/me/settings', params: { org } }
        : { to: '/$org/users/$userUid', params: { org, userUid: result.entity_uid } }
    case 'issue':
      return { to: '/$org/issues/$uid', params: { org, uid: result.entity_uid } }
    case 'capex_plan':
      return { to: '/$org/capex-plans/$planUid', params: { org, planUid: result.entity_uid } }
    case 'contract':
      return { to: '/$org/contracts/$contractUid', params: { org, contractUid: result.entity_uid } }
    case 'tag':
      // The tags browse page resolves its param as a uid OR a code.
      return { to: '/$org/settings/tags/$tagId', params: { org, tagId: result.entity_uid } }
    case 'conversation':
      // The console, not the resident-facing /c/ page — the audience of
      // search is a manager. Every key of the console's search schema is
      // named so the link carries a complete, valid search object.
      return {
        to: '/$org/conversations',
        params: { org },
        search: {
          conversation: result.entity_uid,
          status: undefined,
          channel: undefined,
          resolution: undefined,
          asset: undefined,
          unattached: undefined,
          q: undefined,
          selected: undefined,
          reply_for_issue: undefined,
          message: undefined,
        },
      }
    default:
      return null
  }
}

export function SearchPage() {
  const navigate = useNavigate()
  const params = useParams({ from: '/$org/search' })
  const search = useSearch({ from: '/$org/search' })
  const { request, loading } = useApi()
  const { user } = useAuth()
  const { t } = useTranslation('search')
  const { locale } = useLocale()

  const [query, setQuery] = useState(search.q)
  const [type, setType] = useState<string>(search.entity_types || '(all)')
  const [scope, setScope] = useState<string>(search.scope || 'properties')
  const [tagKeys, setTagKeys] = useState<string[]>(
    search.tags ? search.tags.split(',').map(t => t.trim()).filter(Boolean) : []
  )
  const [includeArchived, setIncludeArchived] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const debouncedQuery = useDebounce(query, 100)

  // The catalogue is capped at 200 tags per organization, so one fetch resolves
  // every key in the URL to a full tag (name + colour) for the chips.
  const { data: allTags = [] } = useTags(params.org)
  const selectedTags = useMemo<TagModel[]>(
    () => tagKeys.map(key =>
      // Match either the code or the uid: the command palette falls back to
      // the uid when it can't resolve a tag's code (race, >200 tags), and a
      // pasted link may carry either form too.
      allTags.find(tag => tag.code === key || tag.uid === key)
        // Not resolved yet (or an unknown code): show the raw key so the chip
        // never disappears, and let the backend answer "no match".
        ?? ({ uid: key, name: key } as TagModel)
    ),
    [tagKeys, allTags]
  )
  const tagsParam = tagKeys.join(',')

  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${params.org}` },
    { title: t('breadcrumb'), path: `/${params.org}/search` }
  ])

  // Update URL when form values change
  const updateUrl = useCallback((newQuery: string, newType: string, newScope: string, newTags: string[]) => {
    const searchParams: any = {}
    if (newQuery) searchParams.q = newQuery
    // Only add entity_types parameter when a specific type is selected (not "(all)")
    if (newType !== '(all)') searchParams.entity_types = newType
    if (newScope !== 'properties') searchParams.scope = newScope
    if (newTags.length > 0) searchParams.tags = newTags.join(',')

    navigate({
      to: '/$org/search',
      params: { org: params.org },
      search: searchParams,
      replace: true
    })
  }, [navigate, params.org])

  // Perform search
  const performSearch = useCallback(async (searchQuery: string, searchType: string, searchScope: string, searchTags: string) => {
    const tagList = searchTags ? searchTags.split(',').filter(Boolean) : []
    // A tag filter alone is a valid search: the API only requires `q` when
    // nothing else narrows the query.
    if ((!searchQuery || searchQuery.length < 2) && tagList.length === 0) {
      setResults([])
      setTotal(0)
      setError(null)
      return
    }

    try {
      setError(null)
      const queryParams: any = {
        scope: searchScope,
        limit: 50,
        offset: 0
      }

      if (searchQuery && searchQuery.length >= 2) {
        queryParams.q = searchQuery
      }

      if (tagList.length > 0) {
        queryParams.tags = tagList
      }

      // Only add entity_types if a specific type is selected (not "(all)")
      if (searchType !== '(all)') {
        queryParams.entity_types = [searchType]
      }

      if (includeArchived) {
        queryParams.include_archived = 'true'
      }

      const response = await request(
        '/api/v1/organizations/{orgCode}/search',
        'GET',
        {
          params: {
            path: { orgCode: params.org },
            query: queryParams
          }
        }
      ) as SearchResponse

      setResults(response.results || [])
      setTotal(response.total || 0)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed'
      setError(errorMessage)
      setResults([])
      setTotal(0)
    }
  }, [request, params.org, includeArchived])

  // Effect to perform search when debounced query changes
  useEffect(() => {
    performSearch(debouncedQuery, type, scope, tagsParam)
  }, [debouncedQuery, type, scope, tagsParam, performSearch])

  // Handle form value changes
  const handleQueryChange = (value: string) => {
    setQuery(value)
    updateUrl(value, type, scope, tagKeys)
  }

  const handleTypeChange = (value: string) => {
    setType(value)
    updateUrl(query, value, scope, tagKeys)
  }

  const handleScopeChange = (value: string) => {
    setScope(value)
    updateUrl(query, type, value, tagKeys)
  }

  const handleClearQuery = () => {
    setQuery('')
    updateUrl('', type, scope, tagKeys)
  }

  const handleTagAdd = (tag: TagModel) => {
    const key = tagKeyOf(tag)
    if (!key || tagKeys.includes(key)) return
    const next = [...tagKeys, key]
    setTagKeys(next)
    updateUrl(query, type, scope, next)
  }

  const handleTagRemove = (tag: TagModel) => {
    const key = tagKeyOf(tag)
    const next = tagKeys.filter(k => k !== key)
    setTagKeys(next)
    updateUrl(query, type, scope, next)
  }

  const renderHighlightedText = (text: string, highlighted?: string) => {
    if (!highlighted || highlighted === text) {
      return text
    }

    // Parse HTML highlights and render safely
    return (
      <span dangerouslySetInnerHTML={{ __html: highlighted }} />
    )
  }

  const showResults = query.length >= 2 || tagKeys.length > 0

  return (
    <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <AppIcon path={mdiMagnify} size="xl" />
            {t('page_title')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('page_description')}
          </p>
        </div>

        {/* Search Form */}
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Search Input */}
              <div className="relative">
                <AppIcon path={mdiMagnify} size="sm" className="absolute left-3 top-1/2 transform -translate-y-1/2" />
                <Input
                  placeholder={t('input_placeholder')}
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  className="pl-10 pr-10"
                  autoFocus
                />
                {query && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearQuery}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  >
                    <AppIcon path={mdiClose} size="sm" />
                  </Button>
                )}
              </div>

              {/* Filters */}
              <div className="flex gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">{t('filter_type_label')}</label>
                  <Select value={type} onValueChange={handleTypeChange}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPE_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {t(`entity_types.${key === '(all)' ? 'all' : key}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">{t('filter_scope_label')}</label>
                  <Select value={scope} onValueChange={handleScopeChange}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCOPE_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {t(`scopes.${key}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2" data-testid="search-tags-filter">
                  <label className="text-sm font-medium">{t('filter_tags_label')}</label>
                  <div className="flex items-center gap-1 flex-wrap">
                    {selectedTags.map((tag) => (
                      <span key={tagKeyOf(tag)} data-testid={`search-tag-chip-${tagKeyOf(tag)}`}>
                        <TagBadgeLink
                          org={params.org}
                          tag={tag}
                          onRemove={() => handleTagRemove(tag)}
                        />
                      </span>
                    ))}
                    <TagPicker
                      orgCode={params.org}
                      selectedTags={selectedTags}
                      onTagAdd={handleTagAdd}
                      onTagRemove={handleTagRemove}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <label htmlFor="include-archived" className="text-sm font-medium cursor-pointer">
                    {t('include_archived')}
                  </label>
                  <input
                    type="checkbox"
                    id="include-archived"
                    checked={includeArchived}
                    onChange={(e) => setIncludeArchived(e.target.checked)}
                    className="h-4 w-4"
                    data-testid="include-archived-checkbox"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
            <span className="ml-2">{t('results.searching')}</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-destructive">
                <AppIcon path={mdiAlertCircleOutline} size="md" />
                <span>{t('error', { message: error })}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {showResults && !loading && !error && (
          <div className="space-y-4">
            {/* Results Counter — zero case is carried by the empty-state card below */}
            {total > 0 && (
              <div className="text-sm text-muted-foreground">
                {t('results.count_found', { count: total })}
              </div>
            )}

            {/* Results List */}
            {results.length > 0 && (
              <div className="space-y-3">
                {results.map((result, index) => {
                  const entityIcon = ENTITY_TYPE_ICONS[result.entity_type]
                  const target = resultTarget(result, params.org, user?.uid)

                  const body = (
                  <div className="flex items-start gap-3">
                    {/* An asset result is drawn by its own type tile
                        (spec 2026-09-17-06) — the family colour is what
                        tells a building from a boiler in a mixed list.
                        Everything else keeps its section glyph. */}
                    {result.entity_type === 'asset' ? (
                      <AssetTypeTile
                        type={result.entity_subtype}
                        size="md"
                        className="mt-0.5"
                      />
                    ) : (
                      <AppIcon path={entityIcon} size="md" className="mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-sm">
                          {renderHighlightedText(result.title, result.highlighted_title)}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {t(`entity_types.${result.entity_type}`)}
                        </Badge>
                        {result.entity_subtype && (
                          <Badge
                            variant="secondary"
                            className="text-xs"
                            data-testid={`search-result-subtype-${result.entity_uid}`}
                          >
                            {getLocalizedName(result.entity_subtype.name, locale) || result.entity_subtype.code}
                          </Badge>
                        )}
                      </div>
                      {result.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {renderHighlightedText(result.description, result.highlighted_description)}
                        </p>
                      )}
                    </div>
                  </div>
                  )

                  // The card IS the link — a real `<a href>`, so middle-click,
                  // cmd+click, "open in new tab" and Tab+Enter all work
                  // (spec 2026-09-22-01). No `cursor-pointer`: an anchor
                  // already carries it.
                  if (target) {
                    return (
                      <Link
                        key={`${result.entity_uid}-${index}`}
                        {...(target as unknown as LinkProps)}
                        className="block rounded-xl border bg-card text-card-foreground shadow-card p-4 hover:shadow-md transition-shadow"
                        data-testid={`search-result-${result.entity_uid}`}
                      >
                        {body}
                      </Link>
                    )
                  }

                  // An entity type the frontend has no route for: still shown,
                  // just not clickable.
                  return (
                    <Card key={`${result.entity_uid}-${index}`} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4" data-testid={`search-result-${result.entity_uid}`}>
                        {body}
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            )}

            {/* Empty State */}
            {total === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <AppIcon path={mdiMagnify} size="2xl" className="mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">{t('no_results.title')}</h3>
                  <p className="text-muted-foreground">
                    {t('no_results.description')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Instructions when no query */}
        {!showResults && !loading && (
          <Card>
            <CardContent className="p-8 text-center">
              <AppIcon path={mdiMagnify} size="2xl" className="mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('empty_state.title')}</h3>
              <p className="text-muted-foreground">
                {t('empty_state.description')}
              </p>
            </CardContent>
          </Card>
        )}
    </div>
  )
}
